import React from 'react';
import type { Candidate } from '@axiom/validation';
import { CandidateSchema } from '@axiom/validation';
import {
  Banner,
  Button,
  Dropdown,
  Flashy,
  FluidButtonLayout,
  Form,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Input,
  Paragraph,
  ParagraphHeader,
  useApi,
} from '@axiom/ui';
import { PronounsDropdownOptions } from '@axiom/const';
import { CandidateProfileIssuesUtils } from '@axiom/utils';

import { CandidateApi } from '../../api/candidate-api';
import { CandidateProfileIssuesApi } from '../../api/candidate-profile-issues-api';

import { ProfileHeaderUtil } from './profile-header-util';
import { ProfileHeaderDisplayNameSchema } from './profile-header-display-name-schema';

export type ProfileHeaderDisplayNameFormProps = {
  candidate: Candidate;
  name?: string;
  onCancel: () => void;
  onSuccess: () => void;
};

export const ProfileHeaderDisplayNameForm = ({
  candidate,
  name = 'PROFILE_HEADER_DISPLAY_NAME_FORM',
  onCancel,
  onSuccess,
}: ProfileHeaderDisplayNameFormProps) => {
  const [{ data: profileIssues }] = useApi(
    CandidateProfileIssuesApi.readCandidateProfileIssues(candidate.id)
  );
  const nameErrors = CandidateProfileIssuesUtils.getNameErrors(
    profileIssues.issues
  );
  return (
    <Form
      name={name}
      schema={ProfileHeaderDisplayNameSchema}
      initialValues={ProfileHeaderUtil.getDisplayNameInitialValues(candidate)}
      onSubmit={async formData => {
        await CandidateApi.updateCandidate(
          candidate.id,
          CandidateSchema.partial().parse(formData)
        );
        await CandidateApi.refreshCandidate(candidate.id);
        await CandidateProfileIssuesApi.refreshCandidateProfileIssues(
          candidate.id
        );
        onSuccess();
      }}
    >
      {({ fireSubmit }) => {
        return (
          <>
            <ParagraphHeader>Display Name</ParagraphHeader>
            <Gutter bottom="16px" />
            {nameErrors.length > 0 && (
              <div data-test="NAME_ERRORS">
                {nameErrors.map(msg => (
                  <Gutter bottom="16px" key={msg}>
                    <Banner impact="low">
                      <ParagraphHeader as="span">
                        <Flashy color="textSecondary">
                          Update your display name.
                        </Flashy>
                      </ParagraphHeader>{' '}
                      {msg}
                    </Banner>
                  </Gutter>
                ))}
              </div>
            )}
            <Paragraph>
              Your display name is the name that appears to Talent Success and
              our clients. This does not need to be your legal name. Additional
              personalizations to avoid:
            </Paragraph>
            <Gutter bottom="16px" />
            <Paragraph>
              <ul>
                <li>Do not use all caps.</li>
                <li>
                  Do not include middle names or initials unless they help
                  clarify your name or are part of your personal brand. If you
                  need to add a middle name or initial, append it to your
                  Display First Name.
                </li>
                <li>
                  Do not include qualifications (JD, MBA, LLM, etc.) in your
                  display name fields.
                </li>
                <li>
                  Do not include titles (Esq., Mr., Mrs., etc.) in your display
                  name fields.
                </li>
              </ul>
            </Paragraph>
            <Grid>
              <GridRow>
                <GridColumn largeScreenWidth={4} smallScreenWidth={12}>
                  <Input name="displayFirstName" label="Display First Name" />
                  <Gutter bottom="16px" only="smallScreen" />
                </GridColumn>
                <GridColumn largeScreenWidth={4} smallScreenWidth={12}>
                  <Input name="displayLastName" label="Display Last Name" />
                  <Gutter bottom="16px" only="smallScreen" />
                </GridColumn>
                <GridColumn largeScreenWidth={4} smallScreenWidth={12}>
                  <Dropdown
                    label="Pronouns"
                    name="pronouns"
                    options={PronounsDropdownOptions}
                    displayKey="display"
                    valueKey="value"
                  />
                  <Gutter bottom="16px" only="smallScreen" />
                </GridColumn>
              </GridRow>
            </Grid>
            <Gutter bottom="16px" />
            <FluidButtonLayout>
              <Button
                variation="outline"
                name="CANCEL"
                onClick={() => {
                  onCancel();
                }}
              >
                Cancel
              </Button>

              <Button name="SAVE" type="submit" onClick={fireSubmit}>
                Save
              </Button>
            </FluidButtonLayout>
          </>
        );
      }}
    </Form>
  );
};
